import { type ComparisonOperator, type Conjunction, Workflow as GraphQlWorkflow } from "@acto/gql/graphql";

export interface WorkflowFilterParam {
  name: string;
  operator: ComparisonOperator | null;
  stringValue: string | null;
  stringValues: string[] | null;
  numericValue: number | null;
  intValue: number | null;
  dateValue: string | null;
  boolValue: boolean | null;
}

export interface WorkflowFilterCondition {
  filter: string;
  params: WorkflowFilterParam[];
}

export interface WorkflowFilterGroup {
  conjunction: Conjunction;
  conditions: WorkflowFilterCondition[];
}

export interface WorkflowFilterQuery {
  groupConjunction: Conjunction;
  groups: WorkflowFilterGroup[];
}

export default class Workflow {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly isActive: boolean,
    public readonly isCustomQuery: boolean,
    public readonly repeat: boolean,
    public readonly activeTodosCount: number,
    public readonly query: WorkflowFilterQuery,
    public readonly description?: string,
    public readonly dueDate?: string,
    public readonly pauseTill?: string,
    public readonly lastChange?: string,
    public readonly fixedResponsibleEmployee?: { id: string; name: string },
  ) {}

  static fromGraphql(data: Omit<GraphQlWorkflow, "todos" | "kpis">): Workflow {
    return new Workflow(
      data.id,
      data.name,
      data.isActive,
      data.isCustomQuery,
      data.repeat,
      data.activeTodosCount,
      Workflow.mapFilterQuery(data.query),
      data.description ?? undefined,
      data.dueDate ?? undefined,
      data.pauseTill ?? undefined,
      data.lastChange ?? undefined,
      data.fixedResponsibleEmployee
        ? { id: data.fixedResponsibleEmployee.id, name: data.fixedResponsibleEmployee.name }
        : undefined,
    );
  }

  private static mapFilterQuery(query: GraphQlWorkflow["query"]): WorkflowFilterQuery {
    return {
      groupConjunction: query.groupConjunction,
      groups: query.groups.map((group) => ({
        conjunction: group.conjunction,
        conditions: group.conditions.map((condition) => ({
          filter: condition.filter,
          params: condition.params.map((param) => ({
            name: param.name,
            operator: param.operator ?? null,
            stringValue: param.stringValue ?? null,
            stringValues: param.stringValues ? [...param.stringValues] : null,
            numericValue: param.numericValue ?? null,
            intValue: param.intValue ?? null,
            dateValue: param.dateValue != null ? String(param.dateValue) : null,
            boolValue: param.boolValue ?? null,
          })),
        })),
      })),
    };
  }
}
